import { Component } from 'react'
import * as debounce from 'lodash/debounce'
import Scrollable, { ScrollableProps } from 'components/atoms/Scrollable'
import { Omit } from 'interfaces'

type InfiniteScrollableProps = Omit<ScrollableProps, 'innerContainerRef'> & {
  hasMore: boolean
  isLoading: boolean
  // when to load, px
  threshold: number
  onLoadMore: () => void
}

export class InfiniteScrollable extends Component<InfiniteScrollableProps> {
  private elNode: HTMLDivElement | null = null

  render () {
    const {
      hasMore, isLoading, threshold,
      onLoadMore,
      ...otherProps
    } = this.props

    return (
      <Scrollable
        innerContainerRef={ this.handleRef }
        { ...otherProps }
      />
    )
  }

  componentDidMount () {
    this.checkIfNotScrollableDebounced()
    if (this.elNode) {
      this.elNode.addEventListener('scroll', this.handleScroll)
    }
  }

  componentDidUpdate () {
    this.checkIfNotScrollableDebounced()
  }

  componentWillUnmount () {
    this.checkIfNotScrollableDebounced.cancel()
    if (this.elNode) {
      this.elNode.removeEventListener('scroll', this.handleScroll)
    }
  }

  // this method should be debounced since
  // document.body.scrollHeight may be from prev page when component mounts
  private checkIfNotScrollableDebounced = debounce(() => {
    const { hasMore, isLoading, onLoadMore } = this.props
    if (!this.elNode || !hasMore || isLoading) {
      return
    }

    // prevent infinity loading for zero height containers
    const allowZeroHeight = this.elNode.childElementCount === 0 || this.elNode.clientHeight > 0
    const isNotScrollable = this.elNode.scrollHeight <= this.elNode.clientHeight && allowZeroHeight

    if (isNotScrollable) {
      onLoadMore()
    }
  }, 15)

  private handleRef = (el: HTMLDivElement | null) => {
    this.elNode = el
  }

  private handleScroll = () => {
    const {
      hasMore,
      isLoading,
      threshold,
      onLoadMore,
    } = this.props
    if (!this.elNode || !hasMore || isLoading) {
      return
    }

    const offset = this.elNode.scrollHeight - this.elNode.scrollTop - this.elNode.offsetHeight
    if (offset !== -1 && offset <= threshold) {
      onLoadMore()
    }
  }
}
